import React from "react";
import { Trash2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { workflowColorStyle } from "./useWorkflowStatuses";

/**
 * Vertical timeline component (chronological).
 * `events` must already be ordered by the parent.
 * `onDelete(eventId)` optional — hides delete button when omitted.
 * `showProcess` — when true, shows the process_number + client_name on each event
 *                 (used by the global timeline tab).
 * `byKey(statusKey)` — optional resolver returning { key, label, color }. When
 *                     omitted, the raw event.status key is shown.
 */
export default function TimelineList({ events, onDelete, showProcess = false, byKey, emptyLabel = "Nenhum evento registrado." }) {
  if (!events || events.length === 0) {
    return (
      <div data-testid="timeline-empty" className="text-center py-10 text-sm text-muted-foreground">
        {emptyLabel}
      </div>
    );
  }

  const fmt = (date, time) => {
    try {
      const d = new Date(`${date}T${time}:00`);
      return d.toLocaleString("pt-BR", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return `${date} ${time}`;
    }
  };

  return (
    <ol data-testid="timeline-list" className="relative border-l-2 border-border ml-3 space-y-4 pl-6 py-2">
      {events.map((e) => {
        const meta = byKey ? byKey(e.status) : { key: e.status, label: e.status || "—", color: "slate" };
        const st = workflowColorStyle(meta.color);
        return (
          <li key={e.id} data-testid={`timeline-event-${e.id}`} className="relative">
            {/* Dot marker on the line */}
            <span
              className={`absolute -left-[34px] top-2 h-3 w-3 rounded-full ring-4 ring-white ${st.dot}`}
              aria-hidden
            />
            <div className="rounded-xl border border-border bg-white px-4 py-3 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium tabular-nums" data-testid={`timeline-event-${e.id}-datetime`}>
                    {fmt(e.event_date, e.event_time)}
                  </span>
                  <Badge className={`border ${st.bg}`} variant="outline"
                         data-testid={`timeline-event-${e.id}-status`}>
                    <span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${st.dot}`} />
                    {meta.label}
                  </Badge>
                  {showProcess && e.process_number && (
                    <Badge variant="outline" className="font-mono text-[10px]"
                           data-testid={`timeline-event-${e.id}-process`}>
                      {e.process_number} · {e.client_name || "—"}
                    </Badge>
                  )}
                </div>
                {onDelete && (
                  <Button
                    data-testid={`timeline-event-${e.id}-delete`}
                    variant="ghost" size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => onDelete(e.id)}
                    title="Excluir evento"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <p className="mt-1.5 text-sm text-foreground whitespace-pre-wrap">
                {e.title && (
                  <span data-testid={`timeline-event-${e.id}-title`} className="block font-semibold text-foreground">
                    {e.title}
                  </span>
                )}
                <span data-testid={`timeline-event-${e.id}-description`}>{e.description}</span>
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground"
                 data-testid={`timeline-event-${e.id}-user`}>
                Registrado por <span className="font-medium">{e.user_name || "—"}</span>
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
